import { useEffect, useState } from 'react';
import { DATA_SET_PAGINATION_COUNT } from 'constants/dataSets';
import { useGetNaceCodeTreeQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import { selectNaceCodes } from 'store/slices/dataSets/selectors';
import { useCreateSectorSpecificTopicMutation, useDeleteSectorSpecificTopicMutation, useGetSpecificListQuery } from 'store/slices/standards/apis/standardsApi';

import SectorSpecificView from './SectorSpecificView';

import type ICreateSpecificRequest from 'store/slices/standards/interfaces/ICreateSpecificRequest';
import type ISectorSpecificItem from 'store/slices/standards/interfaces/ISectorSpecificItem';

function SectorSpecific() {
  const [openModal, setOpenModal] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<number>(-1);
  const [offset, setOffset] = useState(0);
  const [industryItems, setIndustryItems] = useState<ISectorSpecificItem[]>([]);

  const { data: naceCodesData, isLoading: isNaceCodesTreeLoading } = useGetNaceCodeTreeQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: selectNaceCodes(result.data) }),
  });

  const [createSectorSpecificTopic] = useCreateSectorSpecificTopicMutation();
  const [deleteSectorSpecificTopic] = useDeleteSectorSpecificTopicMutation();

  const { data: sectorSpecific, isFetching: isSectorSpecificLoading } = useGetSpecificListQuery({
    offset,
  });

  const hasMore = sectorSpecific?.data.meta?.hasNext || false;

  const onLoadMore = () => {
    if (hasMore) {
      setOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  useEffect(() => {
    if (sectorSpecific && sectorSpecific?.data?.items.length > 0) {
      setIndustryItems((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const newItems = sectorSpecific?.data?.items.filter((item) => !existingIds.has(item.id));
        return [...prev, ...newItems];
      });
    }
  }, [sectorSpecific]);

  const onCancel = () => {
    setOpenModal(false);
    setDeleteModalOpen(false);
    setSelectedItem(-1);
  };

  const onOpenModal = () => {
    setOpenModal(!openModal);
  };

  const onAddIndustry = (sector: ICreateSpecificRequest) => {
    createSectorSpecificTopic(sector);
    setOffset(0);
    onCancel();
  };

  const onDeleteItem = (id: number) => {
    setDeleteModalOpen(true);
    setSelectedItem(id);
  };

  const onDeleteConfirm = () => {
    setIndustryItems((prev) => prev.filter((item) => item.id !== selectedItem));
    deleteSectorSpecificTopic({ id: selectedItem });
    onCancel();
  };

  const loading = isSectorSpecificLoading || isNaceCodesTreeLoading;

  return (
    <SectorSpecificView
      items={industryItems}
      isEmptyList={industryItems.length === 0}
      loading={loading}
      onOpenModal={onOpenModal}
      openModal={openModal}
      onAddIndustry={onAddIndustry}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      onDeleteItem={onDeleteItem}
      onCancel={onCancel}
      isDeleteModalOpen={isDeleteModalOpen}
      onDeleteConfirm={onDeleteConfirm}
      naceCodesData={naceCodesData}
    />
  );
}

export default SectorSpecific;
